import { ColumnType } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import { useModalStore } from "@/store/modalStore";
import { useMonthlyCheckStore } from "@/store/monthlyCheckStore";
import { createQueryString, formatToJapaneseDate } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MonthlyCheckDto } from "../types";
import { MONTHLY_CHECK_SUBMISSTION_STATUS } from "../utils";
import { MONTHLY_CHECK_SUBMISSION_STATUS_STYLES } from "../utils/contants";

export const KEYS_SEARCH_PARAMS = {
  PAGE: "page",
  SEARCH: "theme",
};

export const MONTHLY_INSPECTION_DETAIL_MODAL_NAME =
  "MONTHLY_INSPECTION_DETAIL_MODAL";

const useMonthlyInspectionList = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  const currentPage = Number(params[KEYS_SEARCH_PARAMS.PAGE]) || 1;

  const navigate = useNavigate();

  const isInitialMount = useRef(true);

  const [inputValue, setInputValue] = useState<string>(
    params[KEYS_SEARCH_PARAMS.SEARCH] || ""
  );

  const { openModal } = useModalStore();

  const debouncedSearchTerm = useDebounce(inputValue?.trim(), 500);

  const { monthlyChecks, fetchMonthlyChecks, loading } = useMonthlyCheckStore();

  const handleOpenDetailModal = (id: number) => {
    const paramsString = createQueryString({
      [KEYS_SEARCH_PARAMS.PAGE]: currentPage,
      [KEYS_SEARCH_PARAMS.SEARCH]: inputValue,
    });

    navigate(`/monthly-inspections/${id}?${paramsString}`);
    openModal(MONTHLY_INSPECTION_DETAIL_MODAL_NAME);
  };

  const updateSearchParams = (object: {
    [key: string]: string | undefined;
  }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleUsersKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH]: inputValue });
    }
  };

  const monthlyInspectionListColumns: ColumnType<MonthlyCheckDto>[] = [
    {
      title: "対象月",
      dataIndex: "inspection",
      key: "target_month",
      width: "20.5%",
      align: "left",
      render: (inspection, record) => {
        return (
          <p
            className="truncate text-other_social_linkedin hover:underline hover:opacity-80"
            onClick={() => {
              handleOpenDetailModal(record.result_id);
            }}
          >
            {inspection.applicable_month
              ? `${record.year}年${inspection.applicable_month}月`
              : "-"}
          </p>
        );
      },
    },
    {
      title: "テーマ",
      dataIndex: "inspection",
      key: "theme",
      width: "21.3%",
      align: "left",
      render: (inspection) => {
        return <p className="truncate">{inspection.theme || "-"}</p>;
      },
    },
    {
      title: "提出日",
      dataIndex: "answer_date",
      key: "submission_date",
      width: "21.3%",
      align: "left",
      render: (submit_date) => {
        return (
          <p className="truncate">
            {submit_date ? formatToJapaneseDate(submit_date) : "-"}
          </p>
        );
      },
    },
    {
      title: "回答ステータス",
      dataIndex: "has_answered",
      key: "submission_status",
      width: "21.3%",
      align: "left",
      render: (has_answered: MONTHLY_CHECK_SUBMISSTION_STATUS) => {
        return (
          <div className="flex items-center gap-2">
            <span
              className={
                MONTHLY_CHECK_SUBMISSION_STATUS_STYLES[has_answered].className
              }
            ></span>
            <span>
              {MONTHLY_CHECK_SUBMISSION_STATUS_STYLES[has_answered].title}
            </span>
          </div>
        );
      },
    },
    {
      title: "TFTC確認日",
      dataIndex: "confirmation_date",
      key: "response_date",
      width: "15.6%",
      align: "left",
      render: (verification_date) => {
        return (
          <p className="truncate">
            {verification_date ? formatToJapaneseDate(verification_date) : "-"}
          </p>
        );
      },
    },
  ];

  useEffect(() => {
    fetchMonthlyChecks({
      page: currentPage,
      theme: debouncedSearchTerm,
    });
  }, [currentPage, debouncedSearchTerm]);

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm) {
      updateSearchParams({
        [KEYS_SEARCH_PARAMS.SEARCH]: debouncedSearchTerm,
        [KEYS_SEARCH_PARAMS.PAGE]: "1",
      });
    } else {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  return {
    inputValue,
    setInputValue,
    handleUsersKeyDown,
    updateSearchParams,
    KEYS_SEARCH_PARAMS,
    monthlyInspectionListColumns,
    monthlyChecks,
    currentPage,
    isLoading: loading,
    id,
  };
};

export { useMonthlyInspectionList };
